import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { RefreshCw, Sparkles, Flag, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Business Briefing — the "RUN the business" pulse on Command Center.
 * Strategy, opportunities and risks now live in the Board.
 */

function Skeleton() {
  return (
    <div className="animate-pulse space-y-3" data-testid="briefing-loading">
      <div className="h-5 w-64 rounded-full bg-muted" />
      <div className="h-3 w-full rounded-full bg-muted" />
      <div className="h-3 w-4/5 rounded-full bg-muted" />
      <div className="space-y-2">
        {[0, 1, 2, 3].map((i) => <div key={i} className="h-3 w-full rounded-full bg-muted" />)}
      </div>
    </div>
  );
}

export default function BusinessBriefing() {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai/briefing");
      setBriefing(data.briefing);
    } catch {
      setBriefing(null);
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/ai/briefing/refresh");
      setBriefing(data.briefing);
    } catch {
      /* noop */
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const priorities = Array.isArray(briefing?.priorities) ? briefing.priorities : [];

  return (
    <section
      className="relative overflow-hidden rounded-3xl border border-border bg-card p-5 sm:p-6"
      data-testid="business-briefing"
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-[0.12] blur-3xl" style={{ background: "var(--brand-gradient)" }} aria-hidden="true" />
      <div className="relative mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md brand-gradient-bg text-white">
            <Sparkles className="h-3.5 w-3.5" />
          </span>
          <p className="eyebrow text-brand-teal">Business Briefing</p>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing || loading}
          data-testid="briefing-refresh-btn"
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-60"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {loading ? (
        <Skeleton />
      ) : briefing ? (
        <div className="relative">
          <motion.h2
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-xl font-semibold tracking-tight sm:text-2xl"
            data-testid="briefing-greeting"
          >
            {briefing.greeting}
          </motion.h2>

          {(briefing.state_of_business || briefing.summary) && (
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground" data-testid="briefing-state">
              {briefing.state_of_business || briefing.summary}
            </p>
          )}

          {priorities.length > 0 && (
            <div className="mt-4 rounded-2xl border border-border bg-background/50 p-4" data-testid="briefing-priorities">
              <div className="flex items-center gap-2">
                <Flag className="h-3.5 w-3.5 text-brand-teal" />
                <h3 className="font-display text-xs font-semibold uppercase tracking-wider">What needs your attention today</h3>
              </div>
              <ul className="mt-2 space-y-1.5">
                {priorities.slice(0, 6).map((it, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground" data-testid={`briefing-priority-${i}`}>
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-brand-teal" />
                    <span className="leading-relaxed">{typeof it === "string" ? it : JSON.stringify(it)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-3 flex items-center justify-between gap-2 rounded-xl border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
            <span>Looking for strategy, opportunities or risks?</span>
            <Link
              to="/app/board"
              className="inline-flex items-center gap-1 rounded-full brand-gradient-bg px-3 py-1 text-[11px] font-semibold text-white"
              data-testid="briefing-open-board"
            >
              Open Board <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Your briefing is unavailable right now. Try refreshing in a moment.</p>
      )}
    </section>
  );
}
